import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface HeroItem {
  id: number;
  title: string;
  summary: string;
  category: string;
  date: string;
  image: string;
}

export default function HeroCarousel({ items }: { items: HeroItem[] }) {
  const [index, setIndex] = useState(0);
  const total = items.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  const go = (next: number) => setIndex((next + total) % total);
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - 大岛优香资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent" />

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
            <i className="ri-fire-fill w-4 h-4 flex items-center justify-center" />
            焦点资讯
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug mb-4 animate-fade-up">
            {active.title}
          </h1>
          <p className="text-sm md:text-base text-background-200 leading-relaxed mb-6 max-w-xl line-clamp-2">
            {active.summary}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/news/${active.id}`}
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              阅读详情
            </Link>
            <Link
              to="/news"
              className="px-5 py-2.5 rounded-md bg-background-50/15 backdrop-blur text-background-50 border border-background-50/40 text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              更多资讯
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-20 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一条"
          onClick={() => go(index - 1)}
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一条"
          onClick={() => go(index + 1)}
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-7 left-4 md:left-8 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`查看第 ${i + 1} 条`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-400" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}